import {
	API_ACCESS_REQUEST_STATUSES,
	type ApiAccessRequestStatus,
} from "@repo/shared-primitives"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import type { GridApi } from "ag-grid-community"
import { Columns3 } from "lucide-react"
import * as React from "react"
import { PageHeader, Toolbar, ToolbarSpacer } from "@/components/layout"
import { Button } from "@/components/ui/button"
import { DebouncedSearchInput } from "@/features/admin-grid/DebouncedSearchInput"
import { ApiRequestsAdminGrid } from "@/features/api-requests-admin/ApiRequestsAdminGrid"
import type { ApiRequestsAdminInput } from "@/features/api-requests-admin/api-requests-admin-datasource"
import { RequestsTabs } from "@/features/api-requests-admin/RequestsTabs"
import { SegmentedFilter } from "@/features/api-requests-admin/SegmentedFilter"
import type {
	AdminApiAccessRequest,
	ListApiAccessRequestsInput,
} from "@/types/marketplace.types"

/** `all` lifts the status filter; an absent `status` means the pending backlog. */
const STATUS_FILTERS = [...API_ACCESS_REQUEST_STATUSES, "all"] as const
type StatusFilter = (typeof STATUS_FILTERS)[number]

const DEFAULT_STATUS: StatusFilter = "pending"
const DEFAULT_SORT = "createdAt:asc"
const SORT_FIELDS = ["createdAt", "decidedAt"] as const

export const Route = createFileRoute("/api-requests/")({
	component: ApiRequestsPage,
	validateSearch: (search: Record<string, unknown>) => ({
		page: normalizePage(search.page),
		search: search.search as string | undefined,
		status: normalizeStatus(search.status),
		sort: normalizeSort(search.sort),
	}),
})

function normalizePage(raw: unknown): number | undefined {
	const n = Number(raw)
	if (!Number.isFinite(n)) return undefined
	const p = Math.floor(n)
	return p > 0 ? p : undefined
}

/** Anything not a known status is dropped rather than forwarded to the API. */
function normalizeStatus(raw: unknown): StatusFilter | undefined {
	return STATUS_FILTERS.find((s) => s === raw)
}

/** `field:direction`, both whitelisted; anything else falls back to the default. */
function normalizeSort(raw: unknown): string | undefined {
	if (typeof raw !== "string") return undefined
	const [field, direction] = raw.split(":")
	const validField = SORT_FIELDS.find((f) => f === field)
	if (!validField) return undefined
	if (direction !== "asc" && direction !== "desc") return undefined
	return `${validField}:${direction}`
}

const STATUS_OPTIONS: {
	value: ApiAccessRequestStatus | "all"
	label: string
}[] = [
	{ value: "pending", label: "Pending" },
	{ value: "needs-info", label: "Waiting for applicant" },
	{ value: "approved", label: "Approved" },
	{ value: "rejected", label: "Rejected" },
	{ value: "all", label: "All" },
]

function ApiRequestsPage() {
	const searchParams = useSearch({ from: "/api-requests/" })
	const navigate = useNavigate()

	const pageIndex = (searchParams.page ?? 1) - 1
	const status = searchParams.status ?? DEFAULT_STATUS

	const updateFilters = React.useCallback(
		(updates: Record<string, string | number | undefined>) => {
			navigate({
				to: "/api-requests",
				search: (prev: Record<string, unknown>) => {
					const next = { ...prev, ...updates }
					for (const key of Object.keys(next)) {
						const val = next[key as keyof typeof next]
						if (val === undefined || val === "") {
							delete next[key as keyof typeof next]
						}
					}
					return next as never
				},
				replace: true,
			})
		},
		[navigate],
	)

	const handleDebouncedSearch = React.useCallback(
		(value: string) =>
			updateFilters({ search: value || undefined, page: undefined }),
		[updateFilters],
	)

	const selectStatus = React.useCallback(
		(next: StatusFilter) => {
			updateFilters({
				status: next === DEFAULT_STATUS ? undefined : next,
				page: undefined,
			})
		},
		[updateFilters],
	)

	const [gridApi, setGridApi] = React.useState<GridApi | null>(null)
	const toggleColumns = React.useCallback(() => {
		if (!gridApi) {
			return
		}
		if (gridApi.getOpenedToolPanel() === "columns") {
			gridApi.closeToolPanel()
			return
		}
		gridApi.openToolPanel("columns")
	}, [gridApi])

	const [sortBy, sortOrder] = React.useMemo(() => {
		const [by, order] = (searchParams.sort ?? DEFAULT_SORT).split(":")
		return [
			(by || "createdAt") as ListApiAccessRequestsInput["sortBy"],
			(order || "asc") as ListApiAccessRequestsInput["sortOrder"],
		]
	}, [searchParams.sort])

	const input = React.useMemo<ApiRequestsAdminInput>(
		() => ({
			search: searchParams.search,
			status: status === "all" ? undefined : status,
			sortBy,
			sortOrder,
		}),
		[searchParams.search, status, sortBy, sortOrder],
	)

	const handlePageChange = React.useCallback(
		(nextIndex: number) =>
			updateFilters({ page: nextIndex === 0 ? undefined : nextIndex + 1 }),
		[updateFilters],
	)

	const handleSortChange = React.useCallback(
		(
			by: ListApiAccessRequestsInput["sortBy"],
			order: ListApiAccessRequestsInput["sortOrder"],
		) => {
			const sort = `${by}:${order}`
			updateFilters({ sort: sort === DEFAULT_SORT ? undefined : sort })
		},
		[updateFilters],
	)

	const handleRowClicked = React.useCallback(
		(request: AdminApiAccessRequest) =>
			navigate({
				to: "/api-requests/$requestId",
				params: { requestId: request.id },
			}),
		[navigate],
	)

	return (
		<div className="flex h-[calc(100vh-3rem)] flex-col gap-4 p-6">
			<PageHeader
				title="Requests"
				description="Approving can enable API access, give the Discord roles and create the applicant's integration app"
			/>
			<RequestsTabs />

			<Toolbar>
				<DebouncedSearchInput
					defaultValue={searchParams.search ?? ""}
					onDebouncedChange={handleDebouncedSearch}
					placeholder="Search by username, email, project or Discord…"
				/>
				<SegmentedFilter
					label="Status"
					options={STATUS_OPTIONS}
					value={status}
					onChange={selectStatus}
				/>
				<ToolbarSpacer />
				<Button variant="outline" size="sm" onClick={toggleColumns}>
					<Columns3 className="h-3.5 w-3.5" />
					Columns
				</Button>
			</Toolbar>

			<ApiRequestsAdminGrid
				input={input}
				pageIndex={pageIndex}
				onPageChange={handlePageChange}
				onSortChange={handleSortChange}
				onRowClicked={handleRowClicked}
				onGridReady={setGridApi}
			/>
		</div>
	)
}
